import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, MessageSquare } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../components/site/Seo";
import { Container, SectionHeading, Eyebrow, CtaBand } from "../components/site/Ui";
import { PLANS, MANAGED_SERVICE, PRICING_FAQ, TRIAL } from "../data/site";
import { useLang } from "../lib/LangContext";

export default function Pricing() {
  const { tr } = useLang();
  const [annual, setAnnual] = useState(false);

  return (
    <>
      <Seo
        title="Pricing — 7-day free trial, then from €19/month"
        description="Start with a 7-day free trial of PostMint. Card required, €0 today, cancel any time during the trial. Then Starter €19, Grow €39 or Pro €79 per month, or save two months with annual billing."
        path="/pricing"
      />

      <section className="mesh-warm" data-testid="pricing-hero">
        <Container className="py-16 lg:py-20">
          <Eyebrow>{tr("Pricing")}</Eyebrow>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {tr("Try it free for 7 days. Then from €19 a month.")}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            {tr("PostMint is software, so it costs a fraction of an agency. Start with a full 7-day trial: you add your card, pay €0 today, and can cancel any time during the trial without being charged.")}
          </p>

          <div className="mt-9 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 soft-shadow">
            {[
              { id: "monthly", label: "Monthly" },
              { id: "annual", label: "Annual — 2 months free" },
            ].map((opt) => {
              const on = (opt.id === "annual") === annual;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAnnual(opt.id === "annual")}
                  data-testid={`pricing-toggle-${opt.id}`}
                  aria-pressed={on}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                    on ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {tr(opt.label)}
                </button>
              );
            })}
          </div>
        </Container>
      </section>

      <section className="pb-16" data-testid="pricing-cards">
        <Container>
          <div className="mb-8 grid items-center gap-6 rounded-3xl border border-[#F95738]/40 bg-[#FFF7F4] p-7 sm:p-9 lg:grid-cols-[1.1fr_0.9fr]" data-testid="pricing-trial-band">
            <div>
              <span className="inline-flex rounded-full bg-[#F95738] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                {tr("Start here")}
              </span>
              <h2 className="mt-4 font-display text-2xl font-bold text-slate-900 sm:text-3xl">
                {tr(TRIAL.headline)}
              </h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-600">{tr(TRIAL.tagline)}</p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-500">{tr(TRIAL.afterwards)}</p>
              <Link
                to="/get-started"
                data-testid="pricing-start-trial-button"
                className="mt-6 inline-flex items-center justify-center rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white shadow-[0_10px_30px_rgba(249,87,56,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#E0482B]"
              >
                {tr(TRIAL.cta)}
              </Link>
            </div>
            <ul className="space-y-2.5">
              {TRIAL.features.map((f) => (
                <li key={f} className="flex gap-2.5 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F95738]" aria-hidden="true" />
                  {tr(f)}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3">
            {PLANS.map((p) => {
              const showAnnual = annual && p.annualPrice > 0;
              const perMonth = showAnnual ? Math.round((p.annualPrice / 12) * 100) / 100 : p.price;
              return (
                <article
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  className={`relative flex h-full flex-col rounded-3xl border bg-white p-7 transition-transform duration-200 hover:-translate-y-1 ${
                    p.highlight
                      ? "border-[#F95738] soft-shadow-lg xl:-mt-4 xl:pb-12"
                      : "border-slate-200 soft-shadow"
                  }`}
                >
                  {p.badge && (
                    <span className="absolute -top-3 left-7 rounded-full bg-[#F95738] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                      {tr(p.badge)}
                    </span>
                  )}
                  <h2 className="font-display text-2xl font-bold text-slate-900">{p.name}</h2>
                  <p className="mt-3 flex items-baseline gap-1">
                    <span className="font-display text-4xl font-extrabold text-slate-900">
                      €{showAnnual ? perMonth.toFixed(2).replace(/\.00$/, "") : perMonth}
                    </span>
                    <span className="text-sm text-slate-500">{tr("/ month")}</span>
                  </p>
                  {showAnnual ? (
                    <p className="mt-1.5 text-xs font-semibold text-[#1F8A5F]" data-testid={`pricing-annual-${p.id}`}>
                      €{p.annualPrice} {tr("billed yearly — you save")} €{p.price * 12 - p.annualPrice}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-slate-400">
                      {p.annualPrice > 0 ? `${tr("or")} €${p.annualPrice} ${tr("per year")}` : tr("Free forever")}
                    </p>
                  )}
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{tr(p.tagline)}</p>

                  <ul className="mt-6 flex-1 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-2.5 text-sm text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F95738]" aria-hidden="true" />
                        {tr(f)}
                      </li>
                    ))}
                  </ul>

                  <dl
                    data-testid={`pricing-terms-${p.id}`}
                    className="mt-6 space-y-1.5 rounded-2xl bg-[#FAFAFA] p-4 text-xs leading-relaxed text-slate-600"
                  >
                    {[
                      ["Price", `€${showAnnual ? perMonth.toFixed(2).replace(/\.00$/, "") : perMonth} ${tr("per month")}${showAnnual ? ` (€${p.annualPrice} ${tr("billed once per year")})` : ""}`],
                      ["VAT", tr("Excluding VAT. VAT is calculated and shown at checkout and on your invoice.")],
                      ["Billing period", showAnnual ? tr("Billed yearly, in advance") : tr("Billed monthly, in advance")],
                      ["Free trial", tr("7 days free — card required, €0 today")],
                      ["After the trial", tr("Nothing is charged automatically. Generating, scheduling and publishing pause until you choose a plan.")],
                      ["Renewal", showAnnual ? tr("Renews automatically every year until you cancel") : tr("Renews automatically every month until you cancel")],
                      ["Cancelling", tr("Cancel any time in Dashboard → Subscription. Access continues to the end of the period you paid for.")],
                    ].map(([label, value]) => (
                      <div key={label} className="flex gap-2">
                        <dt className="w-28 shrink-0 font-semibold text-slate-500">{tr(label)}</dt>
                        <dd className="min-w-0 flex-1">{value}</dd>
                      </div>
                    ))}
                  </dl>

                  <Link
                    to={`/get-started?package=${p.id}${annual ? "&billing=annual" : ""}`}
                    data-testid={`pricing-choose-${p.id}`}
                    className={`mt-7 inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-base font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
                      p.highlight
                        ? "bg-[#F95738] text-white shadow-[0_10px_30px_rgba(249,87,56,0.3)] hover:bg-[#E0482B]"
                        : "border border-slate-300 bg-white text-slate-900 hover:border-slate-400"
                    }`}
                  >
                    {tr(p.cta)}
                  </Link>
                </article>
              );
            })}
          </div>

          <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 text-sm leading-relaxed text-slate-600" data-testid="pricing-legal-summary">
            <p className="font-semibold text-slate-900">{tr("Before you subscribe")}</p>
            <ul className="mt-3 space-y-2">
              {[
                "Every plan starts with a 7-day free trial. You add your card at signup, pay €0 today, and your plan starts automatically when the trial ends unless you cancel first — we email you before that.",
                "All prices are shown excluding VAT. The exact VAT treatment is calculated at checkout and shown on your invoice.",
                "Paid plans are billed in advance and renew automatically — monthly plans every month, annual plans every year — until you cancel.",
                "You can cancel any time from Dashboard → Subscription. Your access runs to the end of the period you already paid for.",
                "Reaching a fair-use allowance pauses generating; it never deletes your content.",
              ].map((line) => (
                <li key={line} className="flex gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F95738]" aria-hidden="true" />
                  {tr(line)}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-500">
              {tr("Full details:")}{" "}
              <Link to="/legal/terms" className="font-semibold text-[#F95738] hover:underline">{tr("Terms of Service")}</Link>
              {" · "}
              <Link to="/legal/refund-policy" className="font-semibold text-[#F95738] hover:underline">{tr("Refund & Cancellation Policy")}</Link>
              {" · "}
              <Link to="/legal/privacy-policy" className="font-semibold text-[#F95738] hover:underline">{tr("Privacy Policy")}</Link>
            </p>
          </div>
        </Container>
      </section>

      <section className="border-t border-slate-200 bg-white py-20" data-testid="pricing-managed">
        <Container>
          <div className="grid items-center gap-8 rounded-3xl bg-[#0F172A] p-8 sm:p-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-200">
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                {tr("Optional human service")}
              </span>
              <h2 className="mt-5 text-2xl font-bold text-white sm:text-3xl">{MANAGED_SERVICE.name}</h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">{tr(MANAGED_SERVICE.tagline)}</p>
              <p className="mt-4 text-base font-semibold text-white">
                {tr("From")} €{MANAGED_SERVICE.fromPrice} {tr("/ month, on top of your plan")}
              </p>
              <Link
                to="/contact?topic=Managed"
                data-testid="pricing-managed-contact-button"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-semibold text-slate-900 transition-transform hover:-translate-y-0.5"
              >
                {tr(MANAGED_SERVICE.cta)}
              </Link>
            </div>
            <ul className="space-y-3">
              {MANAGED_SERVICE.features.map((f) => (
                <li key={f} className="flex gap-2.5 text-sm text-slate-200">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#FFC43D]" aria-hidden="true" />
                  {tr(f)}
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      <section className="border-t border-slate-200 py-20" data-testid="pricing-faq">
        <Container>
          <SectionHeading eyebrow="Pricing FAQ" title="Questions about the plans." />
          <Accordion type="single" collapsible className="mt-10 divide-y divide-slate-200">
            {PRICING_FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-0">
                <AccordionTrigger
                  data-testid={`pricing-faq-trigger-${i}`}
                  className="py-5 text-left font-display text-base font-semibold text-slate-900 hover:no-underline"
                >
                  {tr(f.q)}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-slate-600">{tr(f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </section>

      <CtaBand testId="pricing-final-cta-button" cta="Start Free Trial" />
    </>
  );
}
